<template>
    <div class="columns is-gapless pt-2 session">
        <div class="column is-narrow">
            <h6><i class="fad fa-screen-users ml-1 mr-4 has-text-primary"></i></h6>
        </div>
        <div class="column">
            <h6 class="mb-2">{{ session.title }}</h6>
            <div class="session-meta">
                <span>{{ session.start_at | moment('dddd, MMMM Do, h:mm A') }} - {{ session.end_at | moment('h:mm A') }}</span>
                <span v-if="session.presenter">, <i class="fad fa-person-chalkboard ml-1 fa-flip-horizontal"></i> {{ session.presenter }}</span>
            </div>

            <section v-if="session.description && isAllowed(session)">
                <hr style="margin: 1.3rem 0"/>
                <div v-html="session.description"></div>
            </section>

            <section v-if="!stateRegistration.isLocked">
                <hr style="margin: 1.3rem 0"/>

                <div class="mt-4 mb-2">
                    <div v-if="isEnrolled(session.id)">
                        <div class="mt-4">
                            <b-field>
                                <p class="control">
                                    <b-button @click="$emit('schedule')" :disabled="!!registration.is_po_pending"><i class="fas fa-check has-text-success mr-2"></i><span class="is-hidden-mobile">You are registered for this session.</span><span class="is-hidden-desktop">Registered</span></b-button>
                                </p>
                                <p class="control">
                                    <b-button @click="remove(session)" :disabled="!!registration.is_po_pending || processing"><i class="far fa-times mr-2 has-text-warning"></i><span class="is-hidden-mobile">Remove</span></b-button>
                                </p>
                            </b-field>
                        </div>
                    </div>
                    <div v-else>
                        <div v-if="isAllowed(session)">
                            <template v-if="conflicts(session).length">
                                <b-button @click="session.show_conflicts = true" v-if="!session.show_conflicts" :disabled="!!registration.is_po_pending || processing">
                                    <i class="far fa-fw has-text-warning mr-2 fa-calendar-circle-exclamation"></i>Add<span class="is-hidden-mobile"> to Your Schedule</span>
                                    <span v-if="conflicts(session).length"></span>
                                </b-button>

                                <div v-if="session.show_conflicts" class="conflicts">
                                    <div class="mb-5">This session conflicts with the following session(s) already on your schedule:</div>
                                    <div v-for="session in conflicts(session)" class="mt-3 ml-5">
                                        <i class="fad fa-screen-users ml-1 mr-4 has-text-primary"></i><strong>{{ session.title }}</strong>
                                        ({{ session.start_at | moment('dddd, MMMM Do, h:mm A') }} - {{ session.end_at | moment('h:mm A') }})
                                    </div>

                                    <div class="mt-5">
                                        <b-button @click="attend(session)" class="mr-2 is-warning has-background-white" outlined :disabled="processing"><i class="far fa-retweet mr-3"></i>Add this Instead</b-button>
                                        <b-button @click="session.show_conflicts = false">Cancel</b-button>
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="isOverMaxHours(session)">
                                <b-button @click="session.show_conflicts = true" v-if="!session.show_conflicts" :disabled="!!registration.is_po_pending">
                                    <i class="far fa-fw has-text-danger mr-2 fa-calendar-xmark"></i>Add<span class="is-hidden-mobile"> to Your Schedule</span>
                                    <span v-if="conflicts(session).length"></span>
                                </b-button>

                                <div v-if="session.show_conflicts" class="conflicts">
                                    <div class="mb-5">Your registration is limited to a maximum number of sessions or total time. Adding this session would exceed your limit.<br><strong>Remove other session(s) to enable this session.</strong></div>

                                    <div class="mt-5">
                                        <b-button @click="session.show_conflicts = false">Ok</b-button>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <b-button @click="attend(session)" type="is-primary" class="" :disabled="!!registration.is_po_pending || processing">
                                    <template v-if="isConcluded(session)"><i class="far fa-fw fa-calendar-check mr-3"></i>Confirm Attendance<span class="is-hidden-mobile"></span></template>
                                    <template v-else><i class="far fa-fw fa-calendar-circle-plus mr-3"></i>Add<span class="is-hidden-mobile"> to Your Schedule</span></template>
                                </b-button>
                            </template>
                        </div>
                        <div v-else class="is-size-7">
                            <i class="fal fa-ban has-text-warning mr-2"></i>Your registration does not include this session.
                        </div>
                    </div>
                </div>
            </section>
        </div>

    </div>
</template>

<script>
    import moment from 'moment';
    export default {
        name: "ScheduleSessionComponent",
        props: {
            session: Object,
            registration: Object,
            stateRegistration: Object,
            processing: Boolean,
        },
        methods: {
            isEnrolled(sessionId) {
                return this.registration.reg_sessions_ids.includes(sessionId);
            },
            isAllowed(session) {
                return session.allowed_types.includes(this.registration.reg_type_id);
            },
            isConcluded(session) {
                return moment(session.end_at).isBefore(moment());
            },
            conflicts(subjectSession) {
                let conflicts = [];

                if (this.registration.reg_sessions_ids.length && this.registration.conference) {
                    for (let i = 0; i < this.registration.conference.sessions.length; i++) {
                        const session = this.registration.conference.sessions[i];

                        if (session.id === subjectSession.id) continue;

                        if (this.isEnrolled(session.id) && subjectSession.range.overlaps(session.range)) {
                            conflicts.push(session);
                        }
                    }
                    return conflicts;
                } else {
                    return [];
                }
            },
            isOverMaxHours(subjectSession) {
                if (this.registration.reg_type.hour_limit === 0) return false;
                let totalHours = 0;
                for (let i = 0; i < this.registration.reg_sessions_ids.length; i++) {
                    totalHours += this.registration.conference.sessions[i].clock_hours;
                }
                return (totalHours + subjectSession.clock_hours) >= this.registration.reg_type.hour_limit;
            },
            attend: function(session) {
                this.$emit('attend', session);
            },
            remove: function(session) {
                this.$emit('remove', session);
            }
        }
    };
</script>

<style scoped lang="scss">
    .session {
        border: 1px solid #e7e7e7;
        background-color: #fafafa;
        border-radius: 5px;
        padding: 1rem !important;

        .conflicts {
            background-color: white;
            border: 1px solid #dbdbdb;
            padding: 1rem;
            border-radius: 5px;
        }

        .session-meta {
            font-size: 0.85rem;
        }
    }
</style>
